// The Blueprint board's grouping (concept A, Amir 26 Oct; pages/board-tools, "Toolbar and grouping").
// Group in the corner groups the lanes by division with one press, the company's way unless this person
// chose otherwise on this browser; its ▾ menu folds or opens every division, puts them back to the
// company's way, makes theirs the company's (dispatch.openGrouped), and, for owners and managers,
// arranges the divisions on the board itself: lanes dragged by ⋮⋮ (or moved from ⋮⋮'s menu) to their
// home division, headings renamed and reordered (dragged by their own ⋮⋮, their technicians with them,
// or moved up and down from its menu), divisions added from the departments, all saved at
// Done in one action (businessUnits.arrangeDivisions) with one Undo. The first time, the board is laid
// out from each technician's last 30 days of visits (staff.suggestDivisions).
// Anyone who dispatches moves a technician to another division for the day from their menu (Today in),
// or by dragging their name onto a division: at once, with Undo (⌘Z / Ctrl+Z too) and Add why.
// Riverstone keeps its sheets (board-divisions.tsx); none of this is drawn there.

import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { RpcError } from '@flowology/client';
import { uuidv7 } from '@flowology/kernel';
import {
  Button as FlButton,
  DragNote,
  Link as FlLink,
  Menu,
  Popover,
  TextField,
  Toast,
  type BandEditing,
  type DivisionEdit,
  type GroupMenu,
  type LaneGrip,
  type LaneLine,
  type MenuAt,
  type MenuSection,
} from '@flowology/ui-web/fl';
import { client } from './api.ts';
import { pagePath } from './blueprint/places.ts';
import { keepGroupedChoice, readGroupedChoice } from './board-cache.ts';
import {
  REST,
  REST_NAME,
  arrangeInput,
  arrangedLine,
  divisionName,
  divisionSlot,
  draftGroups,
  draftName,
  draftOf,
  labelOf,
  laneGroups,
  moveDivision,
  placeDivision,
  removeDivision,
  slotWords,
  suggestedDraft,
  todayInItems,
  usuallyIn,
  type Draft,
  type DivisionSpan,
  type LaneGroup,
} from './board-divisions-model.ts';
import type { Board, BoardTech } from './board-model.ts';

/** How long a move's or Done's toast, and its Undo, stays (ms). */
export const GROUPING_TOAST_MS = 8000;
/** How far a press moves before it's a drag (px). */
const DRAG_PX = 6;

const MAX_DIVISIONS = 12;

const why = (e: unknown) => (e instanceof RpcError ? e.message : 'That did not work. Try again.');
const firstOf = (name: string) => name.split(' ')[0] || name;
/** "After Service" as it reads mid-sentence: "after Service". */
const lead = (words: string) => words.charAt(0).toLowerCase() + words.slice(1);

interface Unit {
  id: string;
  name: string;
  active: boolean;
}

interface Arranging {
  /** No divisions yet: Set up, laid out from the suggestion. */
  first: boolean;
  /** Started from the suggestion, or empty. */
  from: 'suggestion' | 'empty' | 'now';
  draft: Draft;
  suggestion: Draft | null;
  units: Unit[];
}

interface Note {
  text: string;
  undo?: () => Promise<void> | void;
  /** a move for the day: Add why says its reason */
  move?: { id: string; first: string };
}

type OpenMenu =
  | { kind: 'lane'; techId: string; at: MenuAt }
  | { kind: 'head'; id: string; at: MenuAt }
  | { kind: 'add'; at: MenuAt };

interface Drag {
  techId: string;
  how: 'grip' | 'name';
  x0: number;
  y0: number;
  x: number;
  y: number;
  started: boolean;
  /** the division under the pointer (its data-drop), REST for Everyone else */
  over: string | null;
}

/** A division dragged by its heading's ⋮⋮ while arranging: where it would go (divisionSlot's slot). */
interface HeadDrag {
  id: string;
  x0: number;
  y0: number;
  x: number;
  y: number;
  started: boolean;
  slot: number | null;
}

export interface GroupingArgs {
  tenant: string;
  who: string | undefined;
  permissions: readonly string[];
  /** may give out visits (a move for the day is the dispatcher's, and works while mirroring) */
  canDispatch: boolean;
  /** the day's board once it's loaded */
  board: Board | null;
  date: string;
  isToday: boolean;
  dayText: (date: string) => string;
  reload: () => void;
  /** read out to screen readers */
  say: (words: string) => void;
  folded: ReadonlySet<string>;
  setFolded: (folded: Set<string>) => void;
}

export function useBoardGrouping(a: GroupingArgs) {
  const { board, permissions } = a;
  const techs = board?.technicians ?? [];
  const divisions = board?.divisions ?? [];
  const has = (p: string) => permissions.includes(p);
  // businessUnits.arrangeDivisions (org.edit) with the homes (staff.edit)
  const canArrange = has('org.edit') && has('staff.edit');
  // settings.set on dispatch.openGrouped (settings.edit, and the setting's own org.edit)
  const canSetDefault = has('settings.edit') && has('org.edit');
  const canMoveToday = a.canDispatch && divisions.length > 0;

  const [choice, setChoice] = useState<boolean | null>(() => readGroupedChoice(a.tenant, a.who));
  const companyGrouped = board?.openGrouped ?? true;
  const grouped = divisions.length > 0 && (choice ?? companyGrouped);
  const differs = choice !== null && choice !== companyGrouped;
  const choose = (g: boolean | null) => {
    const next = g === companyGrouped ? null : g;
    setChoice(next);
    keepGroupedChoice(a.tenant, a.who, next);
  };

  const [arr, setArr] = useState<Arranging | null>(null);
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [menu, setMenu] = useState<OpenMenu | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note | null>(null);
  const [whyOpen, setWhyOpen] = useState(false);
  const [whyText, setWhyText] = useState('');
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const dragged = useRef(false);
  const [headDrag, setHeadDrag] = useState<HeadDrag | null>(null);
  const headRef = useRef<HeadDrag | null>(null);
  // the draft as it is now, for a drag's window listeners (they live across renders)
  const arrNow = useRef<Arranging | null>(null);
  arrNow.current = arr;

  const show = (n: Note) => {
    setWhyOpen(false);
    setWhyText('');
    setNote(n);
    a.say(n.text);
  };
  // the toast goes after a while, unless Add why is open on it
  useEffect(() => {
    if (!note || whyOpen) return;
    const t = setTimeout(() => setNote(null), GROUPING_TOAST_MS);
    return () => clearTimeout(t);
  }, [note, whyOpen]);
  // ⌘Z / Ctrl+Z undoes what the toast offers to, while it shows (not while typing)
  useEffect(() => {
    if (!note?.undo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'z' || e.shiftKey || !(e.metaKey || e.ctrlKey)) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest('input, textarea, [contenteditable="true"]')) return;
      e.preventDefault();
      const undo = note.undo;
      setNote(null);
      void undo?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [note]);
  // a new day: nothing stays half arranged or dragged
  useEffect(() => {
    setDrag(null);
    dragRef.current = null;
    setHeadDrag(null);
    headRef.current = null;
    setMenu(null);
  }, [a.date]);

  const forDay = a.isToday ? 'for today' : `for ${a.dayText(a.date)}`;
  const nameOf = (id: string | null | undefined) => divisionName(divisions, id);

  // ── arranging ──
  const focusLater = (selector: string) =>
    requestAnimationFrame(() => document.querySelector<HTMLElement>(selector)?.focus());
  const startArranging = async (focusTech?: string) => {
    if (busy || !board) return;
    const first = divisions.length === 0;
    setBusy(true);
    try {
      const units = (await client.call<{ businessUnits: Unit[] }>('businessUnits.list', {})).businessUnits;
      let suggestion: Draft | null = null;
      if (first) {
        const r = await client.call<{ suggestions: { staffId: string; businessUnitId: string | null }[] }>(
          'staff.suggestDivisions',
          {},
        );
        suggestion = suggestedDraft(r.suggestions, units, MAX_DIVISIONS);
      }
      const useSuggestion = !!suggestion?.divisions.length;
      setArr({
        first,
        from: first ? (useSuggestion ? 'suggestion' : 'empty') : 'now',
        draft: useSuggestion && suggestion ? suggestion : draftOf(divisions, techs),
        suggestion: useSuggestion ? suggestion : null,
        units,
      });
      setTyped({});
      setMenu(null);
      setNote(null);
      focusLater(focusTech ? `[data-lane-grip="${CSS.escape(focusTech)}"]` : '[data-band-mode]');
    } catch (e) {
      show({ text: why(e) });
    } finally {
      setBusy(false);
    }
  };
  const setDraft = (f: (d: Draft) => Draft) => setArr((x) => (x ? { ...x, draft: f(x.draft) } : x));
  /** The draft with every name still being typed kept. */
  const committed = (x: Arranging): Draft => ({
    ...x.draft,
    divisions: x.draft.divisions.map((d) =>
      typed[d.id] === undefined ? d : { ...d, label: labelOf(typed[d.id]!, d.unitName) },
    ),
  });
  const cancel = () => {
    setArr(null);
    setTyped({});
    show({ text: 'Nothing changed.' });
  };
  const done = async () => {
    if (!arr || busy) return;
    const draft = committed(arr);
    const input = arrangeInput(draft, techs);
    const before = draftOf(divisions, techs);
    const same =
      JSON.stringify(input.divisions) ===
        JSON.stringify(before.divisions.map((d) => ({ id: d.id, label: d.label }))) && !input.homes.length;
    if (same) return cancel();
    // Undo: the divisions as they were, and each changed technician's home as it was
    const ids = new Set(before.divisions.map((d) => d.id));
    const back = {
      divisions: before.divisions.map((d) => ({ id: d.id, label: d.label })),
      homes: input.homes.map((h) => {
        const was = techs.find((t) => t.id === h.staffId)?.homeDivisionId ?? null;
        return { staffId: h.staffId, divisionId: was && ids.has(was) ? was : null };
      }),
    };
    setBusy(true);
    try {
      await client.call('businessUnits.arrangeDivisions', input);
      setArr(null);
      setTyped({});
      // they set it up to see it: grouped, whatever they chose before
      if (draft.divisions.length && !grouped) choose(true);
      show({
        text: arrangedLine(draft),
        undo: async () => {
          try {
            await client.call('businessUnits.arrangeDivisions', back);
            show({ text: 'The divisions are back as they were.' });
          } catch (e) {
            show({ text: why(e) });
          }
          a.reload();
        },
      });
      a.reload();
    } catch (e) {
      show({ text: why(e) });
    } finally {
      setBusy(false);
    }
  };

  const band: BandEditing | null = arr
    ? {
        title: arr.first ? 'Set up divisions' : 'Arranging divisions',
        // the first time, the band says so in the system's word for waiting (never red: not a problem)
        ...(arr.first ? { state: 'Not set up yet' } : {}),
        line:
          arr.from === 'suggestion'
            ? 'Each technician is placed by their last 30 days of jobs. Drag anyone by ⋮⋮ to change it, rename a heading, or add a division. Nobody else sees it until Done.'
            : arr.from === 'empty'
              ? 'Add a division under the last lane, then drag each technician onto it.'
              : 'Drag a technician by ⋮⋮ to their home division. Rename or reorder a heading. Everyone’s board changes at Done.',
        controls: (
          <>
            {arr.suggestion ? (
              arr.from === 'suggestion' ? (
                <FlButton
                  onPress={() => setArr({ ...arr, from: 'empty', draft: { divisions: [], homes: {} } })}
                >
                  Start empty
                </FlButton>
              ) : (
                <FlButton onPress={() => setArr({ ...arr, from: 'suggestion', draft: arr.suggestion! })}>
                  Use the suggestion
                </FlButton>
              )
            ) : null}
            <FlButton isDisabled={busy} onPress={cancel}>
              Cancel
            </FlButton>
            <FlButton variant="primary" isDisabled={busy} onPress={() => void done()}>
              Done
            </FlButton>
          </>
        ),
      }
    : null;

  // ── the corner ──
  const runGroupAction = (id: string) => {
    const keys = (groups ?? []).map((g) => g.key);
    if (id === 'fold') a.setFolded(new Set(keys));
    else if (id === 'open') a.setFolded(new Set());
    else if (id === 'reset') choose(null);
    else if (id === 'arrange') void startArranging();
    else if (id === 'make') void makeCompanyWay();
  };
  const makeCompanyWay = async () => {
    const was = companyGrouped;
    try {
      await client.call('settings.set', { key: 'dispatch.openGrouped', value: grouped });
      setChoice(null);
      keepGroupedChoice(a.tenant, a.who, null);
      show({
        text: `Everyone’s board now opens ${grouped ? 'grouped' : 'not grouped'}.`,
        undo: async () => {
          try {
            await client.call('settings.set', { key: 'dispatch.openGrouped', value: was });
            show({ text: `Everyone’s board opens ${was ? 'grouped' : 'not grouped'} again.` });
          } catch (e) {
            show({ text: why(e) });
          }
          a.reload();
        },
      });
      a.reload();
    } catch (e) {
      show({ text: why(e) });
    }
  };
  const way = (g: boolean) => (g ? 'grouped' : 'not grouped');
  const groupMenu: GroupMenu = !divisions.length
    ? {
        title: 'No divisions yet',
        sections: [
          {
            items: [
              canArrange
                ? { id: 'arrange', label: 'Set up divisions on the board…' }
                : {
                    id: 'arrange',
                    label: 'Edit divisions',
                    isDisabled: true,
                    reason: 'No divisions yet. An owner or manager sets them up.',
                  },
            ],
          },
        ],
        onAction: runGroupAction,
      }
    : {
        title: 'Group by division',
        note: `Company default: ${way(companyGrouped)} · yours: ${way(grouped)}`,
        sections: [
          {
            label: 'On your board',
            items: [
              {
                id: 'fold',
                label: 'Fold every division',
                isDisabled: !grouped,
                reason: 'The board isn’t grouped',
              },
              {
                id: 'open',
                label: 'Open every division',
                isDisabled: !grouped,
                reason: 'The board isn’t grouped',
              },
              ...(differs ? [{ id: 'reset', label: 'Back to the company’s way' }] : []),
            ],
          },
          {
            label: 'For everyone',
            items: [
              canArrange
                ? { id: 'arrange', label: 'Arrange divisions on the board…' }
                : {
                    id: 'arrange',
                    label: 'Edit divisions',
                    isDisabled: true,
                    reason:
                      'Owners and managers change divisions. You can move a technician for today from their name.',
                  },
              ...(differs && canSetDefault
                ? [{ id: 'make', label: `Open everyone’s board ${way(grouped)}`, hint: 'like yours' }]
                : []),
            ],
          },
        ],
        onAction: runGroupAction,
      };
  const corner = {
    canGroup: true,
    grouped: arr ? true : grouped,
    onGroupChange: (g: boolean) => {
      if (arr) return;
      if (!divisions.length) {
        if (canArrange) void startArranging();
        return;
      }
      choose(g);
    },
    groupOff: !divisions.length && !canArrange ? 'No divisions yet. An owner or manager sets them up.' : null,
    groupNote: !divisions.length
      ? canArrange
        ? 'Click to set up divisions'
        : ''
      : 'Right-click or ▾ for options',
    groupMenu,
  };

  // ── the groups ──
  const groups: LaneGroup[] | null =
    arr && board ? draftGroups(arr.draft, techs) : board && grouped ? laneGroups(board) : null;

  /** A drop over this group would change something. */
  const lands = (d: Drag | null, key: string): boolean => {
    if (!d?.started || d.over !== key) return false;
    const t = techs.find((x) => x.id === d.techId);
    if (!t) return false;
    if (d.how === 'grip') return (arr?.draft.homes[t.id] ?? REST) !== key;
    return key !== REST && key !== (t.divisionId ?? null);
  };

  /** The group a dragged division would land just before (its mark), while the drop would move it. */
  const slotBefore = (() => {
    if (!arr || !headDrag?.started || headDrag.slot === null) return null;
    const next = placeDivision(arr.draft, headDrag.id, headDrag.slot);
    if (next === arr.draft) return null;
    const at = next.divisions.findIndex((d) => d.id === headDrag.id);
    return next.divisions[at + 1]?.id ?? REST;
  })();

  /** A group's heading, beyond its name and lines: arranging's grip and field, where a drop lands. */
  const headFor = (g: LaneGroup) => {
    const d = arr?.draft.divisions.find((x) => x.id === g.key);
    const edit: DivisionEdit | null =
      arr && d
        ? {
            fieldLabel: `Name on the board for ${d.unitName}`,
            value: typed[d.id] ?? draftName(d),
            onChange: (v) => setTyped((x) => ({ ...x, [d.id]: v })),
            onCommit: () => {
              const v = typed[d.id];
              if (v === undefined) return;
              setDraft((x) => ({
                ...x,
                divisions: x.divisions.map((y) =>
                  y.id === d.id ? { ...y, label: labelOf(v, y.unitName) } : y,
                ),
              }));
              setTyped(({ [d.id]: _, ...rest }) => rest);
            },
            onCancel: () => setTyped(({ [d.id]: _, ...rest }) => rest),
            gripLabel: `Reorder or remove ${draftName(d)}`,
            onGripMenu: (at) => {
              if (!dragged.current) setMenu({ kind: 'head', id: d.id, at });
            },
            gripOpen: menu?.kind === 'head' && menu.id === d.id,
            onGripPointerDown: (e) => beginHeadDrag(d.id, e),
          }
        : null;
    const droppable = arr ? true : canMoveToday && g.key !== REST;
    return {
      edit,
      empty: g.techs.length === 0,
      emptyLine: 'Nobody yet: drag a technician here',
      ...(droppable ? { dropKey: g.key } : {}),
      isTarget: lands(drag, g.key),
      slot: slotBefore === g.key,
      lifted: !!headDrag?.started && headDrag.id === g.key,
    };
  };

  // ── dragging a lane (⋮⋮ while arranging) or a name (onto a division, for the day) ──
  const beginDrag = (techId: string, how: Drag['how'], e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    const d: Drag = {
      techId,
      how,
      x0: e.clientX,
      y0: e.clientY,
      x: e.clientX,
      y: e.clientY,
      started: false,
      over: null,
    };
    dragRef.current = d;
    setDrag(d);
  };
  const dragging = drag !== null;
  useEffect(() => {
    if (!dragging) return;
    const overAt = (x: number, y: number) =>
      document.elementFromPoint(x, y)?.closest('[data-drop]')?.getAttribute('data-drop') ?? null;
    const move = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const started = d.started || Math.hypot(e.clientX - d.x0, e.clientY - d.y0) > DRAG_PX;
      const next = {
        ...d,
        x: e.clientX,
        y: e.clientY,
        started,
        over: started ? overAt(e.clientX, e.clientY) : null,
      };
      dragRef.current = next;
      setDrag(next);
    };
    const up = () => {
      const d = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!d?.started) return;
      // the click that ends a drag doesn't open the name's or the grip's menu
      dragged.current = true;
      setTimeout(() => (dragged.current = false), 0);
      if (d.over) drop(d.techId, d.how, d.over);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      dragRef.current = null;
      setDrag(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', esc as unknown as EventListener);
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', esc as unknown as EventListener);
      window.removeEventListener('keydown', esc);
    };
    // drop reads the current board through this render's closure; a drag lives within one board
  }, [dragging]);

  // ── dragging a division by its heading's ⋮⋮ (arranging): its technicians go with it ──
  const beginHeadDrag = (id: string, e: ReactPointerEvent) => {
    if (e.button !== 0 || !arr) return;
    const d: HeadDrag = {
      id,
      x0: e.clientX,
      y0: e.clientY,
      x: e.clientX,
      y: e.clientY,
      started: false,
      slot: null,
    };
    headRef.current = d;
    setHeadDrag(d);
  };
  const headDragging = headDrag !== null;
  useEffect(() => {
    if (!headDragging) return;
    // every division's box, heading and lanes, in the draft's order (none moves until the drop)
    const spans = (): DivisionSpan[] =>
      (arrNow.current?.draft.divisions ?? []).flatMap((d) => {
        const el = document.querySelector(`.fl-division[data-drop="${CSS.escape(d.id)}"]`);
        const r = el?.getBoundingClientRect();
        return r ? [{ top: r.top, bottom: r.bottom }] : [];
      });
    const move = (e: PointerEvent) => {
      const d = headRef.current;
      if (!d) return;
      const started = d.started || Math.hypot(e.clientX - d.x0, e.clientY - d.y0) > DRAG_PX;
      const next = {
        ...d,
        x: e.clientX,
        y: e.clientY,
        started,
        slot: started ? divisionSlot(spans(), e.clientY) : null,
      };
      headRef.current = next;
      setHeadDrag(next);
    };
    const up = () => {
      const d = headRef.current;
      headRef.current = null;
      setHeadDrag(null);
      if (!d?.started) return;
      // the click that ends a drag doesn't open the heading's menu
      dragged.current = true;
      setTimeout(() => (dragged.current = false), 0);
      const x = arrNow.current;
      if (d.slot === null || !x) return;
      const words = slotWords(x.draft, d.id, d.slot);
      const div = x.draft.divisions.find((v) => v.id === d.id);
      if (!words || !div) return;
      setDraft((y) => placeDivision(y, d.id, d.slot!));
      a.say(`${draftName(div)} moved: ${lead(words)}.`);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      headRef.current = null;
      setHeadDrag(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', esc as unknown as EventListener);
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', esc as unknown as EventListener);
      window.removeEventListener('keydown', esc);
    };
  }, [headDragging]);

  const setHome = (techId: string, key: string) =>
    setDraft((x) => ({ ...x, homes: { ...x.homes, [techId]: key === REST ? null : key } }));
  const drop = (techId: string, how: Drag['how'], key: string) => {
    const t = techs.find((x) => x.id === techId);
    if (!t) return;
    if (how === 'grip') return setHome(techId, key);
    if (key === REST || key === (t.divisionId ?? null)) return;
    if (t.divisionMove && key === (t.homeDivisionId ?? null)) void back(t);
    else void moveToday(t, key);
  };

  /** The note by the pointer while dragging: where letting go puts them. */
  const dragNote = (() => {
    if (headDrag?.started && arr) {
      const d = arr.draft.divisions.find((x) => x.id === headDrag.id);
      if (!d) return null;
      const words = headDrag.slot === null ? null : slotWords(arr.draft, d.id, headDrag.slot);
      return (
        <DragNote
          at={{ x: headDrag.x + 16, y: headDrag.y + 16 }}
          head={words ? `${draftName(d)}: ${lead(words)}` : `Move ${draftName(d)}`}
          foot={words ? 'With its technicians' : 'Drag above or below another division'}
        />
      );
    }
    if (!drag?.started) return null;
    const t = techs.find((x) => x.id === drag.techId);
    if (!t) return null;
    let head: string;
    let line: string | null = null;
    if (drag.how === 'grip') {
      const name = drag.over
        ? drag.over === REST
          ? REST_NAME
          : draftName(
              arr?.draft.divisions.find((d) => d.id === drag.over) ?? {
                id: '',
                unitName: REST_NAME,
                label: null,
              },
            )
        : null;
      head = name ? `Home: ${name}` : 'Drag onto a division';
      line = name ? 'From today on, everyone’s board' : null;
    } else if (!drag.over || drag.over === REST) head = 'Drag onto a division';
    else if (drag.over === (t.divisionId ?? null)) head = `Already in ${nameOf(drag.over)}`;
    else if (t.divisionMove && drag.over === (t.homeDivisionId ?? null)) {
      head = `Back to ${nameOf(drag.over)}`;
      line = 'their home';
    } else {
      head = `In ${nameOf(drag.over)} ${forDay}`;
      line = `Their home stays ${nameOf(t.homeDivisionId)}`;
    }
    return <DragNote at={{ x: drag.x + 16, y: drag.y + 16 }} head={head} {...(line ? { foot: line } : {})} />;
  })();

  // ── moves for the day ──
  const moveToday = async (t: BoardTech, divisionId: string) => {
    const id = uuidv7();
    const was = t.divisionId ?? null;
    const first = firstOf(t.name);
    try {
      await client.call('staff.moveDivisionForDay', { id, staffId: t.id, date: a.date, divisionId });
      show({
        text: `${first} is in ${nameOf(divisionId)} ${forDay}. Their home stays ${nameOf(t.homeDivisionId)}.`,
        move: { id, first },
        undo: async () => {
          try {
            await client.call('staff.undoDivisionMove', { id });
            show({ text: `${t.name} is back in ${nameOf(was)} ${forDay}.` });
          } catch (e) {
            show({ text: why(e) });
          }
          a.reload();
        },
      });
      a.reload();
    } catch (e) {
      show({ text: why(e) });
    }
  };
  const back = async (t: BoardTech) => {
    const m = t.divisionMove;
    if (!m) return;
    try {
      await client.call('staff.undoDivisionMove', { id: m.id });
      show({
        text: `${t.name} is back in ${nameOf(t.homeDivisionId)} ${forDay}.`,
        undo: () => moveToday(t, m.divisionId),
      });
      a.reload();
    } catch (e) {
      show({ text: why(e) });
    }
  };
  const saveWhy = async () => {
    const m = note?.move;
    const reason = whyText.trim();
    if (!m) return;
    if (!reason) return setWhyOpen(false);
    try {
      await client.call('staff.explainDivisionMove', { id: m.id, reason });
      show({ text: 'Saved with the move.' });
      a.reload();
    } catch (e) {
      show({ text: why(e) });
    }
  };

  /** A lane's extras: ⋮⋮ while arranging, the dashed key and "Usually in" when moved, faded when lifted. */
  const laneFor = (t: BoardTech) => {
    const grip: LaneGrip | null = arr
      ? {
          label: `Move ${t.name} to another division`,
          onMenu: (at) => {
            if (!dragged.current) setMenu({ kind: 'lane', techId: t.id, at });
          },
          onPointerDown: (e) => beginDrag(t.id, 'grip', e),
          isOpen: menu?.kind === 'lane' && menu.techId === t.id,
        }
      : null;
    const moved = !arr && grouped && !!t.divisionMove;
    const usually = moved ? usuallyIn(t, divisions) : null;
    return {
      grip,
      moved,
      lifted: !!drag?.started && drag.techId === t.id,
      usually: usually ? ({ words: usually } satisfies LaneLine) : null,
      /** on the lane's wrapper: a name pressed and dragged onto a division moves them for the day */
      wrap: {
        onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
          if (arr || !grouped || !canMoveToday || e.pointerType === 'touch') return;
          if (!(e.target as Element).closest('[data-lane-name]')) return;
          beginDrag(t.id, 'name', e);
        },
        onClickCapture: (e: ReactMouseEvent<HTMLElement>) => {
          if (!dragged.current) return;
          e.preventDefault();
          e.stopPropagation();
        },
      },
    };
  };

  // ── the technician's menu: Today in, and (owners and managers) their home ──
  const techMore = (t: BoardTech): MenuSection[] => {
    if (arr) return [];
    const out: MenuSection[] = [];
    const today = canMoveToday ? todayInItems(t, divisions) : null;
    if (today?.items.length) out.push(today);
    if (canArrange && divisions.length)
      out.push({
        label: 'Home division',
        items: [{ id: 'home', label: 'Change home on the board…', hint: nameOf(t.homeDivisionId) }],
      });
    return out;
  };
  /** Runs a menu choice of techMore's; false when it isn't one. */
  const techAction = (t: BoardTech, id: string): boolean => {
    if (id === 'today:back') void back(t);
    else if (id.startsWith('today:')) void moveToday(t, id.slice('today:'.length));
    else if (id === 'home') void startArranging(t.id);
    else return false;
    return true;
  };

  // ── "+ Add a division", under the last lane while arranging ──
  const addRef = useRef<HTMLDivElement>(null);
  const addRow: ReactNode = arr ? (
    <div className="bp-div-add" ref={addRef}>
      <FlLink onPress={() => addRef.current && setMenu({ kind: 'add', at: { element: addRef.current } })}>
        + Add a division
      </FlLink>
      <span>From your departments. Everyone else stays last.</span>
    </div>
  ) : null;

  // ── the menus: a heading's ⋮⋮, a lane's ⋮⋮, Add a division ──
  const menuEl = (() => {
    if (!menu || !arr) return null;
    const close = () => setMenu(null);
    if (menu.kind === 'head') {
      const i = arr.draft.divisions.findIndex((d) => d.id === menu.id);
      const d = arr.draft.divisions[i];
      if (!d) return null;
      const last = i === arr.draft.divisions.length - 1;
      return (
        <Menu
          at={menu.at}
          onClose={close}
          label={`${draftName(d)}’s heading`}
          sections={[
            {
              items: [
                { id: 'up', label: 'Move up', isDisabled: i === 0, reason: 'It’s first' },
                {
                  id: 'down',
                  label: 'Move down',
                  isDisabled: last,
                  reason: 'Everyone else always comes after it',
                },
              ],
            },
            { items: [{ id: 'remove', label: 'Remove this division' }] },
          ]}
          onAction={(id) => {
            if (id === 'up' || id === 'down') setDraft((x) => moveDivision(x, d.id, id === 'up' ? -1 : 1));
            if (id === 'remove') {
              const was = arr.draft;
              const n = Object.values(was.homes).filter((h) => h === d.id).length;
              setDraft((x) => removeDivision(x, d.id));
              show({
                text: `${draftName(d)} is no longer a division. Its ${n} ${n === 1 ? 'technician is' : 'technicians are'} in Everyone else.`,
                undo: () => setDraft(() => was),
              });
            }
          }}
        />
      );
    }
    if (menu.kind === 'lane') {
      const t = techs.find((x) => x.id === menu.techId);
      if (!t) return null;
      const home = arr.draft.homes[t.id] ?? null;
      return (
        <Menu
          at={menu.at}
          onClose={close}
          label={`${t.name}’s home division`}
          title="Home division"
          sections={[
            {
              items: [
                ...arr.draft.divisions.map((d) => ({
                  id: d.id,
                  label: draftName(d),
                  checked: home === d.id,
                })),
                { id: REST, label: REST_NAME, checked: home === null },
              ],
            },
          ]}
          onAction={(id) => setHome(t.id, id)}
        />
      );
    }
    const taken = new Set(arr.draft.divisions.map((d) => d.id));
    const free = arr.units
      .filter((u) => u.active && !taken.has(u.id))
      .sort((x, y) => x.name.localeCompare(y.name));
    const full = arr.draft.divisions.length >= MAX_DIVISIONS;
    return (
      <Menu
        at={menu.at}
        onClose={close}
        label="Add a division"
        title="Add a division"
        note="From your departments"
        sections={[
          {
            items: free.length
              ? free.map((u) => ({
                  id: u.id,
                  label: u.name,
                  isDisabled: full,
                  reason: `${MAX_DIVISIONS} is the most a board groups by`,
                }))
              : [
                  {
                    id: 'none',
                    label: 'Every department is a division',
                    isDisabled: true,
                    // where departments are kept, in the nav's words (QA-767)
                    reason: `Add one under ${pagePath('company')}`,
                  },
                ],
          },
        ]}
        onAction={(id) => {
          const u = free.find((x) => x.id === id);
          if (!u) return;
          setDraft((x) => ({
            ...x,
            divisions: [...x.divisions, { id: u.id, unitName: u.name, label: null }],
          }));
          setArr((x) => (x && x.from === 'empty' ? { ...x, from: 'now' } : x));
        }}
      />
    );
  })();

  // ── the toast: what just happened, Undo, and for a move Add why ──
  const toast: ReactNode = note ? (
    <Toast
      {...(note.undo || note.move
        ? {
            actions: (
              <>
                {note.undo ? (
                  <FlButton
                    size="sm"
                    onPress={() => {
                      const undo = note.undo;
                      setNote(null);
                      void undo?.();
                    }}
                  >
                    Undo
                  </FlButton>
                ) : null}
                {note.move ? (
                  <Popover
                    label={`Why is ${note.move.first} moving?`}
                    isOpen={whyOpen}
                    onOpenChange={setWhyOpen}
                    placement="top start"
                    trigger={<FlButton size="sm">Add why</FlButton>}
                  >
                    <TextField
                      label="For the move’s record (optional)"
                      value={whyText}
                      onChange={setWhyText}
                      placeholder="Short in drains"
                      autoFocus
                    />
                    <FlButton variant="primary" size="sm" onPress={() => void saveWhy()}>
                      Save
                    </FlButton>
                  </Popover>
                ) : null}
              </>
            ),
          }
        : {})}
    >
      {note.text}
    </Toast>
  ) : null;

  return {
    grouped,
    arranging: arr !== null,
    groups,
    corner,
    band,
    headFor,
    laneFor,
    addRow,
    techMore,
    techAction,
    overlay: (
      <>
        {menuEl}
        {toast}
        {dragNote}
      </>
    ),
  };
}
